import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BodyText, Button, HeadingText } from '@paypalcorp/pp-react';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _indexOf from 'lodash/indexOf';

import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { getQueryValue } from '../../lib/location-helper';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import {
  selectDefaultViolation,
  topicTcodeMapping,
  getRcKeyNameForL1Selection,
  getRcKeyNameForL2Selection,
} from '../../lib/PPMEmailUtils';

import { contactChannels, urlConstants } from '../../common/commonConstants';
import { WebForm } from '../components/common';
import { preserveDataNoIntent, sendEmailNoIntent } from '../actions/action-email-form-no-intent';
import { loginFromEmailPage } from '../actions/action-login-modal';
import dispatchLogin from '../actions/action-login-redirect';
import { SELECT_VIOLATION } from './EmailForm';

// USE PP-REACT
function AupViolationEmailForm() {
  const { fields, isMessageSending, kanaCode, subTopicName, topicName } = useSelector((state) => state.emailNoIntent);
  const { topicTreeSelectionL1 } = useSelector((state) => state.topicTree);
  const { channels } = useSelector((state) => state.contactChannels);
  const dispatch = useDispatch();

  const [isReportClicked, reportClicked] = useState(false);

  const aupViolation = {
    aupViolationPageTitle: i18n.message('smarthelp/paypalmeForm.paypalform.pageHeading'),
    aupViolationPageQuestion: i18n.message('smarthelp/paypalmeForm.paypalform.pageQuestion'),
    aupViolationPageReporting: i18n.message('smarthelp/paypalmeForm.paypalform.pageReporting'),
    aupViolationPageButtonText: i18n.message('smarthelp/paypalmeForm.paypalform.buttonText'),
    aupViolationPageDisputeQuestion: i18n.message('smarthelp/paypalmeForm.paypalform.disputeQuestion'),
    aupViolationPageVisitText: i18n.message('smarthelp/paypalmeForm.paypalform.visitText', {
      contactUsUrl: urlConstants.CONTACT_US_URL,
      resolutionLink: Page.getResolutionURL(),
    }),
    aupViolationPageLoginText: i18n.message('smarthelp/paypalmeForm.paypalform.loginText'),
    aupViolationPageInquiryText: i18n.message('smarthelp/paypalmeForm.paypalform.inquiryText', {
      helpLink: urlConstants.HELP_CENTER_URL,
      contactUsUrl: urlConstants.CONTACT_US_URL,
    }),
    aupViolationSendEmail: i18n.message('smarthelp/emailUs.emailUs.submit'),
    aupViolationEmailError: i18n.message('smarthelp/emailUs.emailUs.errors'),
    aupViolationGenerosityTopic: i18n.message(
      'smarthelp/paypalmeForm.paypalform.selection.L1.options.generosityNetwork',
    ),
  };

  const getCustomFieldPayLoad = (formFields) => {
    if (!Array.isArray(formFields) || formFields.length < 1) {
      return [];
    }
    return formFields.map((field) => ({
      name: Page.webformNameReconstuctor(field.name),
      value: field.value,
    }));
  };

  const getCustomFields = (formData) => {
    let formFields = [];
    let payload = [];

    if (!_isEmpty(formData) && formData.length > 1) {
      formFields = formData.filter((field) => {
        const fieldTypes = ['text', 'number', 'textarea', 'date', 'select'];
        if (_indexOf(fieldTypes, field.type) > -1) {
          if (field.type === 'textarea' && field.label === 'Message') {
            return false;
          }
          return true;
        }
        return false;
      });

      payload = getCustomFieldPayLoad(formFields);
    }

    return payload;
  };

  /* eslint-disable consistent-return */
  const submitEmailForm = (formData) => {
    if (isMessageSending) {
      return;
    }
    const messageField = _find(formData, { type: 'textarea' });
    const payload = {
      message: _get(messageField, 'value', ''),
      topic_code: kanaCode,
      subject: subTopicName,
      topicName,
      subTopicName,
      custom_fields: getCustomFields(formData),
      _csrf: Page.getCSRFtoken(),
    };

    // SMTP logout Email form require email, first_name, last_name in the request body to validate request

    // HACK: PayPalMe Emailform for firsName, lastName and email
    const formLength = formData.length;
    payload.first_name = formData[formLength - 3].value;
    payload.last_name = formData[formLength - 2].value;
    payload.email = formData[formLength - 1].value;

    const venmoQuery = getQueryValue('reqFromVenmo', window.location);
    const venmoImageUrl = window.venmo_img_url || '';

    // Adding image url to payload if available and only when reqFromVenmo query param exists
    if (venmoQuery === 'true' && venmoImageUrl) {
      trackPage({ id: 'aupviolationVenmoImage', link: 'smarthelp-aupviolation-webform-venmo-image-available' });
      payload.image_url = venmoImageUrl;
    }

    // Update topic Ref id base on the PayPalMe second layer topic selection
    const primarySelection = _find(formData, (field) => field.name === SELECT_VIOLATION);
    const secondarySelection = _find(formData, (field) => field.name === 'select_violation_type_all');
    if (primarySelection && primarySelection.value && secondarySelection && secondarySelection.value) {
      // Get the RC key name for L1 and L2 selections
      const l1SelectionRCKeyName = getRcKeyNameForL1Selection(primarySelection.value);
      const l2SelectionRCKeyName = getRcKeyNameForL2Selection(secondarySelection.value);
      payload.topic_code = topicTcodeMapping(l1SelectionRCKeyName, l2SelectionRCKeyName);
    }
    trackLink({
      id: 'aupviolationWebformEmailFormSubmit',
      link: `smarthelp-submit-email-aupviolation-form|${topicName}|${kanaCode}`,
    });

    const topicTreeSelection = topicTreeSelectionL1 ? topicTreeSelectionL1.ref_id : '';
    const selectedDetails = {
      topicId: kanaCode,
      kanaCode,
      topicName,
      subTopicName,
      pageflow: '',
    };

    const hasCommunityAccess = channels.find((channel) => channel.type === contactChannels.COMMUNITY);

    const thankyouPageDescription = Page.getEmailThankYouPageDescription(topicTreeSelection, selectedDetails);
    return dispatch(
      sendEmailNoIntent({
        emailPayload: payload,
        hasCommunityAccess,
        isSmcEnabled: Page.getIsSmcEnabled(),
        thankyouPageDescription,
      }),
    );
  };

  const loginClicked = () => {
    trackLink({ id: 'aupviolationWebformLoginClicked', link: `email-login-aupviolation-webform|${kanaCode}` });
    const currentURI = Page.getCurrentLocationURL();
    dispatch(loginFromEmailPage());
    dispatch(dispatchLogin(currentURI));
  };

  const reportBtnClicked = () => {
    reportClicked(true);
    trackLink({ id: 'aupviolationWebformReportClicked', link: `aupviolation-webform-report-btn-clicked|${kanaCode}` });
    // scrolling down to form elements when report button is clicked
    window.scroll({
      top: 1000,
      behavior: 'smooth',
    });
  };

  /**
   * Added FPTI for link clicks in the content
   */
  const linkClickHandler = (event, innerText) => {
    // event.stopImmediatePropagation();
    trackLink({
      id: 'aupviolationWebformContentClicked',
      link: `smarthelp-${innerText}-link-clicked-on-aupviolation-webform-Page|${kanaCode}`,
    });
  };

  /**
   * Parsing Link content
   */
  const parseLinkContent = (event = {}) => {
    const linkContent = event.target && event.target.href;
    try {
      if (!linkContent) {
        return;
      }
      linkClickHandler(event, linkContent);
    } catch (e) {
      // suppress error
    }
  };

  const renderDisputeInstructions = () => {
    const pageInfoAttrs = {
      className: 'aup_violation_page__page_body__dispute_body_text',
      'data-testid': 'aup-violation-dispute-body-text',
    };

    const innerHTML = Page.getHTMLElement({ message: aupViolation.aupViolationPageVisitText, useHTML: true });
    return (
      <BodyText {...pageInfoAttrs} onClick={(e) => parseLinkContent(e)}>
        {innerHTML}
      </BodyText>
    );
  };

  const renderInquiryInstructions = () => {
    const pageInfoAttrs = {
      className: 'aup_violation_page__page_body__inquiry_text',
      'data-testid': 'aup-violation-inquiry-text',
    };

    const innerHTML = Page.getHTMLElement({ message: aupViolation.aupViolationPageInquiryText, useHTML: true });
    return (
      <BodyText {...pageInfoAttrs} onClick={(e) => parseLinkContent(e)}>
        {innerHTML}
      </BodyText>
    );
  };

  return (
    <div className="aup_violation_page">
      <HeadingText size="md" className="aup_violation_page__header_text">
        {aupViolation.aupViolationPageTitle}
      </HeadingText>
      <div className="aup_violation_page__page_body">
        <HeadingText
          size="sm"
          className="aup_violation_page__page_body__header_text"
          data-testid="aup-violation-heading-text"
        >
          {aupViolation.aupViolationPageQuestion}
        </HeadingText>
        <BodyText className="aup_violation_page__page_body__body_text" data-testid="aup-violation-body-text">
          {aupViolation.aupViolationPageReporting}
        </BodyText>
        <div className="aup_violation_page__page_body__button">
          <Button
            className="aup_violation_page__page_body__button_text"
            data-testid="aup-violation-report-btn"
            onClick={() => reportBtnClicked()}
          >
            {aupViolation.aupViolationPageButtonText}
          </Button>
        </div>
        <HeadingText
          size="sm"
          className="aup_violation_page__page_body__dispute_question"
          data-testid="aup-violation-dispute-heading-text"
        >
          {aupViolation.aupViolationPageDisputeQuestion}
        </HeadingText>
        {renderDisputeInstructions()}
        {!Page.isLoggedIn() && (
          <div className="aup_violation_page__page_body__button">
            <Button
              className="aup_violation_page__page_body__login_btn"
              data-testid="aup-violation-login-btn"
              onClick={() => loginClicked()}
            >
              {aupViolation.aupViolationPageLoginText}
            </Button>
          </div>
        )}
        {renderInquiryInstructions()}
      </div>
      {(isReportClicked || selectDefaultViolation()) && (
        <div className="form-fields">
          <WebForm
            fields={fields}
            submitLabel={aupViolation.aupViolationSendEmail}
            errors={aupViolation.aupViolationEmailError}
            handleFieldValue={() => {
              dispatch(preserveDataNoIntent);
            }}
            handleSubmit={(formData) => submitEmailForm(formData)}
            hasCancelButton={false}
          />
        </div>
      )}
    </div>
  );
}

export default AupViolationEmailForm;
